import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import useElementToPdf from '../utils/useElementToPdf';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import ReferralHistorySheet from '../components/ReferralHistorySheet';
import ReceiptAttachmentsTab from '../components/loyalty/ReceiptAttachmentsTab';
import { AwardRequestsTab, PrintRequestsTab } from '../components/loyalty/RequestsTabs';
import AbuseAttemptsTab from '../components/loyalty/AbuseAttemptsTab';
import OverrideCodesTab from '../components/loyalty/OverrideCodesTab';
import OverrideQuotasTab from '../components/loyalty/OverrideQuotasTab';
import {
  Award,
  UserPlus,
  Search,
  Loader2,
  Plus,
  Minus,
  X,
  Edit2,
  Trash2,
  Save,
  Crown,
  Star,
  Sparkles,
  Gem,
  Phone,
  TrendingUp,
  History,
  AlertTriangle,
  Copy,
  Calendar,
  RefreshCw,
  Printer,
  Users,
  FileText,
} from 'lucide-react';

const LEVEL_ICONS = {
  bronze: <Award className="w-4 h-4" />,
  silver: <Star className="w-4 h-4" />,
  gold: <Sparkles className="w-4 h-4" />,
  black_elite: <Gem className="w-4 h-4" />,
};

const LoyaltyAdmin = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isArabic = language === 'ar';
  const isAdmin = user?.role === 'admin';

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [levels, setLevels] = useState([]);

  // Member form
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '', initial_points: 50, referrer_membership_number: '' });
  const [saving, setSaving] = useState(false);

  // Award form
  const [awardMember, setAwardMember] = useState(null);
  const [awardData, setAwardData] = useState({ points: '', service_name: '', amount_aed: '', note: '', reason: '' });
  const [awarding, setAwarding] = useState(false);

  // Redeem form
  const [redeemMember, setRedeemMember] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  // History modal
  const [historyMember, setHistoryMember] = useState(null);
  const [history, setHistory] = useState([]);
  const [extensionsHistory, setExtensionsHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState('points'); // points | extensions

  // Referral modal
  const [referralMember, setReferralMember] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [canPrintReferral, setCanPrintReferral] = useState(false);
  const [referralPrintMode, setReferralPrintMode] = useState('summary'); // summary | detailed
  const referralPrintRef = useRef(null);
  const handlePrintReferral = useElementToPdf({
    contentRef: referralPrintRef,
    documentTitle: `referral_history_${referralMember?.membership_number || 'member'}_${referralPrintMode}`,
    pageStyle: '@page { size: A4 portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; margin: 0; } }',
  });

  // Tabs
  const [activeTab, setActiveTab] = useState('members'); // members | barber-stats | activity | award-requests
  const [barberStats, setBarberStats] = useState([]);
  const [activity, setActivity] = useState({ transactions: [], activities: [] });
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingDetail, setPendingDetail] = useState({ members_pending: 0, extensions_pending: 0, awards_pending: 0, print_requests_pending: 0, receipt_attachments_pending: 0 });
  const [awardRequests, setAwardRequests] = useState([]);
  const [awardRequestsFilter, setAwardRequestsFilter] = useState('pending'); // all|pending|approved|rejected
  
  // Print requests
  const [printRequests, setPrintRequests] = useState([]);
  const [printRequestsFilter, setPrintRequestsFilter] = useState('pending');

  // Receipt attachments (recent membership ↔ revenue)
  const [receiptAttachments, setReceiptAttachments] = useState([]);
  const [receiptAttachmentsFilter, setReceiptAttachmentsFilter] = useState('pending');

  const fetchReceiptAttachments = async () => {
    try {
      const status = receiptAttachmentsFilter === 'all' ? undefined : receiptAttachmentsFilter;
      const res = await api.get(`/loyalty/receipt-attachments${status ? `?status=${status}` : ''}`);
      setReceiptAttachments(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل تحميل ربط الإيرادات' : 'Failed to load attachments'));
    }
  };

  const decideReceiptAttachment = async (attId, approved, reason = '') => {
    try {
      const url = approved
        ? `/loyalty/receipt-attachments/${attId}/approve`
        : `/loyalty/receipt-attachments/${attId}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`;
      await api.post(url);
      toast.success(approved ? (isArabic ? 'تم الاعتماد' : 'Approved') : (isArabic ? 'تم الرفض' : 'Rejected'));
      fetchReceiptAttachments();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const approveMember = async (m) => {
    try {
      await api.post(`/loyalty/members/${m.id}/approve`);
      toast.success(isArabic ? 'تم الاعتماد' : 'Approved');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const requestExtension = async (m) => {
    if (!window.confirm(isArabic ? `طلب تمديد متتالية ${m.name} 20 يوماً؟` : `Request 20-day extension for ${m.name}?`)) return;
    try {
      const res = await api.post(`/loyalty/members/${m.id}/request-extension`);
      toast.success(res.data?.message || 'OK');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const approveExtension = async (m, approved) => {
    try {
      const url = approved
        ? `/loyalty/members/${m.id}/approve-extension`
        : `/loyalty/members/${m.id}/reject-extension`;
      const res = await api.post(url);
      toast.success(res.data?.message || (approved ? 'اعتُمد' : 'رُفض'));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const fetchAdminTabs = async () => {
    if (!isAdmin) return;
    try {
      const [s, a, p] = await Promise.all([
        api.get('/loyalty/by-barber-stats'),
        api.get('/loyalty/recent-activity?limit=50'),
        api.get('/loyalty/pending-count'),
      ]);
      setBarberStats(s.data || []);
      setActivity(a.data || { transactions: [], activities: [] });
      setPendingCount(p.data?.count || 0);
      setPendingDetail({
        members_pending: p.data?.members_pending || 0,
        extensions_pending: p.data?.extensions_pending || 0,
        awards_pending: p.data?.awards_pending || 0,
        print_requests_pending: p.data?.print_requests_pending || 0,
      });
    } catch (err) {
      // ignore
    }
  };

  const fetchAwardRequests = async (filter = awardRequestsFilter) => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await api.get('/loyalty/award-requests', { params });
      setAwardRequests(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const decideAwardRequest = async (req, approved) => {
    try {
      if (approved) {
        await api.post(`/loyalty/award-requests/${req.id}/approve`);
        toast.success(isArabic ? `تم الاعتماد وإضافة ${req.points} نقطة` : `Approved (+${req.points} pts)`, { duration: 4000 });
      } else {
        const reason = window.prompt(isArabic ? 'سبب الرفض (اختياري):' : 'Reason for rejection (optional):', '');
        if (reason === null) return;
        await api.post(`/loyalty/award-requests/${req.id}/reject`, null, { params: reason ? { rejection_reason: reason } : {} });
        toast.success(isArabic ? 'تم الرفض' : 'Rejected');
      }
      fetchAwardRequests();
      fetchAll();
      fetchAdminTabs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const deleteAwardRequest = async (req) => {
    if (!window.confirm(isArabic ? 'هل تريد حذف هذا الطلب؟' : 'Delete this request?')) return;
    try {
      await api.delete(`/loyalty/award-requests/${req.id}`);
      toast.success(isArabic ? 'تم الحذف' : 'Deleted');
      fetchAwardRequests();
      fetchAdminTabs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const fetchPrintRequests = async (filter = printRequestsFilter) => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await api.get('/loyalty/referral-print-requests', { params });
      setPrintRequests(res.data || []);
    } catch (err) {
      // ignore
    }
  };

  const decidePrintRequest = async (req, approved) => {
    try {
      if (approved) {
        await api.post(`/loyalty/referral-print-requests/${req.id}/approve`);
        toast.success(isArabic ? 'تم اعتماد طلب الطباعة (48 ساعة)' : 'Print request approved (48h)');
      } else {
        const reason = window.prompt(isArabic ? 'سبب الرفض (اختياري):' : 'Reason (optional):', '');
        if (reason === null) return;
        await api.post(`/loyalty/referral-print-requests/${req.id}/reject`, null, { params: reason ? { rejection_reason: reason } : {} });
        toast.success(isArabic ? 'تم الرفض' : 'Rejected');
      }
      fetchPrintRequests();
      fetchAdminTabs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };


  const openReferralModal = async (m) => {
    setReferralMember(m);
    setReferralData(null);
    setCanPrintReferral(false);
    setReferralPrintMode('summary');
    setReferralLoading(true);
    try {
      const [details, canPrint] = await Promise.all([
        api.get(`/loyalty/members/${m.id}/referral-details`),
        api.get(`/loyalty/members/${m.id}/can-print-referral`),
      ]);
      setReferralData(details.data);
      setCanPrintReferral(!!canPrint.data?.can_print);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setReferralLoading(false);
    }
  };

  const requestReferralPrint = async () => {
    if (!referralMember) return;
    try {
      const res = await api.post(`/loyalty/members/${referralMember.id}/request-referral-print`);
      toast.success(res.data?.message || 'تم');
      // refresh can-print flag
      const cp = await api.get(`/loyalty/members/${referralMember.id}/can-print-referral`);
      setCanPrintReferral(!!cp.data?.can_print);
      if (isAdmin) fetchAdminTabs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };


  useEffect(() => {
    if (isAdmin) fetchAdminTabs();
    // eslint-disable-next-line
  }, [isAdmin, members.length]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [membersRes, statsRes, levelsRes] = await Promise.all([
        api.get('/loyalty/members', { params: { q: search || undefined } }),
        api.get('/loyalty/stats'),
        api.get('/loyalty/levels'),
      ]);
      setMembers(membersRes.data || []);
      setStats(statsRes.data);
      setLevels(levelsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); fetchAll(); };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', notes: '', initial_points: 50, referrer_membership_number: '' });
    setShowForm(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name || '', phone: m.phone || '', email: m.email || '', notes: m.notes || '', initial_points: 0, referrer_membership_number: m.referrer_membership_number || '' });
    setShowForm(true);
  };

  const saveMember = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(isArabic ? 'الاسم والجوال مطلوبان' : 'Name & phone required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/loyalty/members/${editing.id}`, {
          name: form.name, phone: form.phone, email: form.email || null, notes: form.notes || null,
        });
        toast.success(isArabic ? 'تم التحديث' : 'Updated');
      } else {
        const payload = { ...form };
        if (!payload.referrer_membership_number) delete payload.referrer_membership_number;
        const res = await api.post('/loyalty/members', payload);
        toast.success(`${isArabic ? 'تم إنشاء العضوية' : 'Member created'} #${res.data.membership_number}`);
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(isArabic ? `حذف ${m.name}؟ سيتم حذف كل سجلاته.` : `Delete ${m.name}?`)) return;
    try {
      await api.delete(`/loyalty/members/${m.id}`);
      toast.success(isArabic ? 'تم الحذف' : 'Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const submitAward = async () => {
    const points = parseInt(awardData.points);
    if (!points || points <= 0) {
      toast.error(isArabic ? 'عدد النقاط مطلوب' : 'Points required');
      return;
    }
    if (!isAdmin && !awardData.reason.trim()) {
      toast.error(isArabic ? 'يجب كتابة سبب إضافة النقاط' : 'A reason is required');
      return;
    }
    setAwarding(true);
    try {
      const res = await api.post(`/loyalty/members/${awardMember.id}/award`, {
        points,
        service_name: awardData.service_name || null,
        amount_aed: awardData.amount_aed ? parseFloat(awardData.amount_aed) : null,
        note: awardData.note || null,
        reason: awardData.reason || null,
      });
      // Distinguish pending vs immediate
      if (res.data?.status === 'pending') {
        toast.success(res.data.message || (isArabic ? 'تم إرسال الطلب للمدير' : 'Request submitted'), { duration: 5000 });
      } else {
        toast.success(res.data.message, { duration: res.data.streak_bonus ? 6000 : 3000 });
      }
      setAwardMember(null);
      setAwardData({ points: '', service_name: '', amount_aed: '', note: '', reason: '' });
      fetchAll();
      if (isAdmin) {
        fetchAdminTabs();
        if (activeTab === 'award-requests') fetchAwardRequests();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setAwarding(false);
    }
  };

  const submitRedeem = async () => {
    const points = parseInt(redeemPoints);
    if (!points || points <= 0) return;
    setRedeeming(true);
    try {
      const res = await api.post(`/loyalty/members/${redeemMember.id}/redeem`, { points });
      toast.success(res.data.message);
      setRedeemMember(null);
      setRedeemPoints('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setRedeeming(false);
    }
  };

  const openHistory = async (m) => {
    setHistoryMember(m);
    setHistoryTab('points');
    try {
      const [txs, exts] = await Promise.all([
        api.get(`/loyalty/members/${m.id}/transactions`),
        api.get(`/loyalty/members/${m.id}/extensions`),
      ]);
      setHistory(txs.data || []);
      setExtensionsHistory(exts.data || []);
    } catch (err) {
      toast.error('فشل تحميل السجل');
    }
  };

  const filteredMembers = useMemo(() => members, [members]);

  const copyMembership = (num) => {
    navigator.clipboard.writeText(num).then(() => toast.success(isArabic ? 'تم النسخ' : 'Copied'));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{isArabic ? 'برنامج الولاء والنقاط' : 'Loyalty Program'}</h1>
            <p className="text-muted-foreground text-sm">
              {isArabic ? 'تسجيل العملاء، إضافة نقاط، إدارة المستويات والمزايا' : 'Members, points, levels & rewards'}
            </p>
          </div>
        </div>
        <Button onClick={openAdd} className="gap-2" data-testid="add-member-btn">
          <UserPlus className="w-4 h-4" />
          {isArabic ? 'إضافة عضو' : 'Add Member'}
        </Button>
      </div>

      {/* Tabs (admin sees extra tabs) */}
      {isAdmin && (
        <div className="flex gap-2 border-b border-white/10 pb-2 flex-wrap">
          {[
            { id: 'members', label: isArabic ? 'الأعضاء' : 'Members', badge: pendingDetail.members_pending + pendingDetail.extensions_pending },
            { id: 'award-requests', label: isArabic ? 'طلبات النقاط' : 'Award Requests', badge: pendingDetail.awards_pending },
            { id: 'receipt-attachments', label: isArabic ? 'ربط الإيرادات' : 'Receipt Links', badge: pendingDetail.receipt_attachments_pending },
            { id: 'abuse-attempts', label: isArabic ? 'المحاولات المشبوهة' : 'Suspicious Attempts', badge: 0 },
            { id: 'override-codes', label: isArabic ? 'رموز الإذن' : 'Override Codes', badge: 0 },
            { id: 'override-quotas', label: isArabic ? 'حصص الإذن' : 'Override Quotas', badge: 0 },
            { id: 'print-requests', label: isArabic ? 'طلبات الطباعة' : 'Print Requests', badge: pendingDetail.print_requests_pending },
            { id: 'barber-stats', label: isArabic ? 'إحصائيات الحلاقين' : 'Per Barber' },
            { id: 'activity', label: isArabic ? 'آخر التعديلات' : 'Recent Activity' },
          ].map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={activeTab === t.id ? 'default' : 'ghost'}
              onClick={() => {
                setActiveTab(t.id);
                if (t.id === 'award-requests') fetchAwardRequests();
                if (t.id === 'print-requests') fetchPrintRequests();
                if (t.id === 'receipt-attachments') fetchReceiptAttachments();
              }}
              data-testid={`loyalty-tab-${t.id}`}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="ms-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse">
                  {t.badge}
                </span>
              )}
            </Button>
          ))}
        </div>
      )}

      {/* Stats */}
      {stats && activeTab === 'members' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 bg-card border-white/10">
            <div className="text-xs text-muted-foreground">{isArabic ? 'إجمالي الأعضاء' : 'Total Members'}</div>
            <div className="text-2xl font-bold">{stats.total_members}</div>
          </Card>
          <Card className="p-4 bg-card border-white/10">
            <div className="text-xs text-muted-foreground">{isArabic ? 'نقاط نشطة' : 'Active Points'}</div>
            <div className="text-2xl font-bold text-amber-500">{stats.total_points_active.toLocaleString()}</div>
          </Card>
          {levels.map((lv) => (
            <Card key={lv.key} className="p-4 bg-card border-white/10" style={{ borderColor: lv.color + '55' }}>
              <div className="text-xs flex items-center gap-1" style={{ color: lv.color }}>
                {LEVEL_ICONS[lv.key]} {isArabic ? lv.name_ar : lv.name_en}
              </div>
              <div className="text-xl font-bold">{stats.by_level[lv.key] || 0}</div>
              <div className="text-[10px] text-muted-foreground">+{lv.bonus_pct}% {isArabic ? 'بونص' : 'bonus'}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      {(!isAdmin || activeTab === 'members') && (
      <Card className="p-3 bg-card border-white/10">
        <form onSubmit={onSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isArabic ? 'بحث بالاسم/الجوال/رقم العضوية...' : 'Search...'}
              className="ps-10 bg-secondary/50 border-white/10"
              data-testid="loyalty-search"
            />
          </div>
          <Button type="submit" variant="outline">{isArabic ? 'بحث' : 'Search'}</Button>
          <Button type="button" variant="ghost" onClick={() => { setSearch(''); setTimeout(fetchAll, 0); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </form>
      </Card>
      )}

      {/* Members table */}
      {(!isAdmin || activeTab === 'members') && (
      <Card className="overflow-hidden bg-card border-white/10">
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Crown className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {isArabic ? 'لا يوجد أعضاء' : 'No members'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="loyalty-members-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-3 px-3">{isArabic ? 'الرقم' : 'Membership #'}</th>
                  <th className="text-start py-3 px-3">{isArabic ? 'الاسم' : 'Name'}</th>
                  <th className="text-start py-3 px-3">{isArabic ? 'الجوال' : 'Phone'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'تاريخ التسجيل' : 'Joined'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'المستوى' : 'Level'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'نقاط نشطة' : 'Points'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'رصيد' : 'Credit'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'زيارات' : 'Visits'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'متتالية' : 'Streak'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'تنتهي قريباً' : 'Expiring'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'إجراء' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => (
                  <tr key={m.id} className="border-t border-white/5 hover:bg-secondary/20" data-testid={`member-row-${m.id}`}>
                    <td className="py-3 px-3">
                      <button onClick={() => copyMembership(m.membership_number)} className="font-mono font-bold text-primary inline-flex items-center gap-1 hover:underline">
                        {m.membership_number}
                        <Copy className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="py-3 px-3 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{m.name}</span>
                        {m.status === 'pending' && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 font-bold animate-pulse"
                            title={isArabic ? `قيد الاعتماد - سجّله: ${m.created_by_name || '-'}` : 'Pending'}
                          >
                            {isArabic ? 'قيد الاعتماد' : 'Pending'}
                          </span>
                        )}
                      </div>
                      {m.created_by_name && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {isArabic ? 'سجّله:' : 'By:'} {m.created_by_name}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground">
                      <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                        <Phone className="w-3 h-3" />{m.phone}
                      </a>
                    </td>
                    <td className="py-3 px-3 text-center text-xs">
                      {m.joined_at ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="font-medium">
                            {new Date(m.joined_at).toLocaleDateString(isArabic ? 'ar-AE' : 'en-GB')}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {(() => {
                              const days = Math.floor((Date.now() - new Date(m.joined_at).getTime()) / 86400000);
                              if (days < 1) return isArabic ? 'اليوم' : 'today';
                              if (days < 30) return `${days} ${isArabic ? 'يوم' : 'd'}`;
                              const months = Math.floor(days / 30);
                              return `${months} ${isArabic ? 'شهر' : 'mo'}`;
                            })()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: m.level.color + '22', color: m.level.color }}
                      >
                        {LEVEL_ICONS[m.level.key]}
                        {isArabic ? m.level.name_ar : m.level.name_en}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center font-bold">{m.points_balance.toLocaleString()}</td>
                    <td className="py-3 px-3 text-center text-emerald-500">{m.redeemable_aed} د.إ</td>
                    <td className="py-3 px-3 text-center">{m.visits_count}</td>
                    <td className="py-3 px-3 text-center">
                      {m.streak_months > 0 ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                              m.streak_status?.at_risk
                                ? 'bg-red-500/20 text-red-500 animate-pulse'
                                : m.streak_status?.extension_active
                                  ? 'bg-blue-500/20 text-blue-500'
                                  : 'bg-orange-500/15 text-orange-500'
                            }`}
                            title={
                              m.streak_status?.at_risk
                                ? `${isArabic ? 'في خطر — مرّ' : 'At risk — '} ${m.streak_status.days_since_last_visit} ${isArabic ? 'يوم بدون زيارة' : 'days idle'}`
                                : m.streak_status?.extension_active
                                  ? `${isArabic ? 'تمديد ساري — باقي' : 'Extended —'} ${m.streak_status.extension_remaining_days} ${isArabic ? 'يوم' : 'd left'}`
                                  : `${m.streak_months} ${isArabic ? 'شهر متواصل' : 'consecutive months'}`
                            }
                          >
                            🔥 {m.streak_months}
                            {m.streak_status?.at_risk && ' ⚠'}
                            {m.streak_status?.extension_active && ` +${m.streak_status.extension_remaining_days}d`}
                          </span>
                          {m.streak_status?.extension_pending && (
                            <span className="text-[9px] text-amber-500 font-bold animate-pulse">
                              {isArabic ? 'تمديد قيد الاعتماد' : 'Extension pending'}
                            </span>
                          )}
                          {m.streak_status?.at_risk && !m.streak_status?.extension_pending && !m.streak_status?.extension_active && (
                            <button
                              type="button"
                              onClick={() => requestExtension(m)}
                              className="text-[9px] text-blue-500 hover:underline"
                              data-testid={`req-ext-${m.id}`}
                            >
                              {isArabic ? `طلب تمديد ${20} يوم` : 'Request +20d'}
                            </button>
                          )}
                          {m.streak_status?.extension_pending && isAdmin && (
                            <div className="flex gap-1 mt-0.5">
                              <button onClick={() => approveExtension(m, true)} className="text-[10px] text-emerald-500 font-bold" data-testid={`appr-ext-${m.id}`}>✓ اعتماد</button>
                              <button onClick={() => approveExtension(m, false)} className="text-[10px] text-red-500 font-bold">✗ رفض</button>
                            </div>
                          )}
                        </div>
                      ) : '-'}
                      {m.next_streak_milestone && !m.streak_status?.at_risk && (
                        <div className="text-[9px] text-muted-foreground mt-0.5">
                          {isArabic ? `+${m.next_streak_milestone.bonus_points} عند ${m.next_streak_milestone.months} شهر` : `+${m.next_streak_milestone.bonus_points} at ${m.next_streak_milestone.months}m`}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {m.expiring_soon_points > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-500 text-xs">
                          <AlertTriangle className="w-3 h-3" />{m.expiring_soon_points}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {m.status === 'pending' && isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-500"
                            onClick={() => approveMember(m)}
                            title={isArabic ? 'اعتماد العضو' : 'Approve'}
                            data-testid={`approve-member-${m.id}`}
                          >
                            ✓
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-emerald-500" onClick={() => setAwardMember(m)} title={isArabic ? 'إضافة نقاط' : 'Award'} disabled={m.status !== 'approved'} data-testid={`award-${m.id}`}>
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-blue-500" onClick={() => setRedeemMember(m)} title={isArabic ? 'استبدال' : 'Redeem'} disabled={m.points_balance < 100 || m.status !== 'approved'} data-testid={`redeem-${m.id}`}>
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openHistory(m)} title={isArabic ? 'السجل' : 'History'} data-testid={`history-${m.id}`}>
                          <History className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-amber-500" onClick={() => openReferralModal(m)} title={isArabic ? 'سجل الإحالة' : 'Referrals'} data-testid={`referral-${m.id}`}>
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-${m.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeMember(m)} data-testid={`del-${m.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {/* Tab: Award Requests (pending point additions from barbers) */}
      {isAdmin && activeTab === 'award-requests' && (
        <AwardRequestsTab
          isArabic={isArabic}
          requests={awardRequests}
          filter={awardRequestsFilter}
          setFilter={setAwardRequestsFilter}
          onFetch={fetchAwardRequests}
          onDecide={decideAwardRequest}
          onDelete={deleteAwardRequest}
        />
      )}


      {/* Tab: Print Requests (referral history print approval) */}
      {isAdmin && activeTab === 'print-requests' && (
        <PrintRequestsTab
          isArabic={isArabic}
          requests={printRequests}
          filter={printRequestsFilter}
          setFilter={setPrintRequestsFilter}
          onFetch={fetchPrintRequests}
          onDecide={decidePrintRequest}
          onOpenReferral={(r) => {
            const m = members.find((x) => x.id === r.member_id);
            if (m) openReferralModal(m);
            else toast.error(isArabic ? 'العضو غير موجود' : 'Member not found');
          }}
        />
      )}

      {/* Tab: Receipt Attachments (recent membership ↔ revenue) */}
      {isAdmin && activeTab === 'receipt-attachments' && (
        <ReceiptAttachmentsTab
          isArabic={isArabic}
          attachments={receiptAttachments}
          filter={receiptAttachmentsFilter}
          setFilter={setReceiptAttachmentsFilter}
          onRefresh={fetchReceiptAttachments}
          onApprove={(id) => decideReceiptAttachment(id, true)}
          onReject={(id, reason) => decideReceiptAttachment(id, false, reason)}
        />
      )}

      {/* Tab: Suspicious Attempts */}
      {isAdmin && activeTab === 'abuse-attempts' && (
        <AbuseAttemptsTab isArabic={isArabic} />
      )}

      {isAdmin && activeTab === 'override-codes' && (
        <OverrideCodesTab isArabic={isArabic} />
      )}

      {isAdmin && activeTab === 'override-quotas' && (
        <OverrideQuotasTab isArabic={isArabic} />
      )}

      {/* Tab: Per-barber stats */}
      {isAdmin && activeTab === 'barber-stats' && (
        <Card className="overflow-hidden bg-card border-white/10" data-testid="barber-stats-panel">
          {barberStats.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {isArabic ? 'لا توجد بيانات بعد' : 'No data yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-start py-3 px-3">{isArabic ? 'المسجِّل' : 'Registered by'}</th>
                    <th className="text-start py-3 px-3">{isArabic ? 'الدور' : 'Role'}</th>
                    <th className="text-center py-3 px-3">{isArabic ? 'الإجمالي' : 'Total'}</th>
                    <th className="text-center py-3 px-3">{isArabic ? 'المعتمد' : 'Approved'}</th>
                    <th className="text-center py-3 px-3">{isArabic ? 'قيد الاعتماد' : 'Pending'}</th>
                  </tr>
                </thead>
                <tbody>
                  {barberStats.map((b) => (
                    <tr key={b.user_id || b.user_name} className="border-t border-white/5">
                      <td className="py-3 px-3 font-medium">{b.user_name}</td>
                      <td className="py-3 px-3 text-xs text-muted-foreground">
                        {b.user_role === 'admin' ? (isArabic ? 'مدير' : 'Admin') : b.user_role === 'barber' ? (isArabic ? 'حلاق' : 'Barber') : b.user_role}
                      </td>
                      <td className="py-3 px-3 text-center font-bold">{b.total}</td>
                      <td className="py-3 px-3 text-center text-emerald-500">{b.approved}</td>
                      <td className="py-3 px-3 text-center text-amber-500">{b.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Tab: Recent activity */}
      {isAdmin && activeTab === 'activity' && (
        <Card className="overflow-hidden bg-card border-white/10" data-testid="recent-activity-panel">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <History className="w-4 h-4" />
              {isArabic ? 'آخر تعديلات بطاقات الولاء' : 'Recent loyalty card edits'}
            </h3>
            <Button size="sm" variant="ghost" onClick={fetchAdminTabs} className="gap-1">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
          {/* Transactions */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-2 px-3">{isArabic ? 'التاريخ' : 'Date'}</th>
                  <th className="text-start py-2 px-3">{isArabic ? 'العضو' : 'Member'}</th>
                  <th className="text-start py-2 px-3">{isArabic ? 'الإجراء' : 'Action'}</th>
                  <th className="text-center py-2 px-3">{isArabic ? 'النقاط' : 'Points'}</th>
                  <th className="text-start py-2 px-3">{isArabic ? 'بواسطة' : 'By'}</th>
                </tr>
              </thead>
              <tbody>
                {activity.transactions.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">-</td></tr>
                ) : activity.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-white/5">
                    <td className="py-2 px-3 text-xs text-muted-foreground">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{t.member_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">#{t.membership_number}</div>
                    </td>
                    <td className={`py-2 px-3 font-semibold ${t.type === 'earn' ? 'text-emerald-500' : 'text-blue-500'}`}>
                      {t.type === 'earn' ? (isArabic ? 'إضافة نقاط' : 'Earn') : (isArabic ? 'استبدال' : 'Redeem')}
                      {t.service_name && <div className="text-[10px] text-muted-foreground font-normal">{t.service_name}</div>}
                    </td>
                    <td className="py-2 px-3 text-center font-bold">{t.type === 'earn' ? '+' : '-'}{t.points}</td>
                    <td className="py-2 px-3 text-xs">{t.created_by_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full space-y-3" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editing ? (isArabic ? 'تعديل عضو' : 'Edit') : (isArabic ? 'عضو جديد' : 'New Member')}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isArabic ? 'الاسم *' : 'Name *'} data-testid="member-name" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={isArabic ? 'الجوال * (+9715XXXXXXXX)' : 'Phone *'} data-testid="member-phone" />
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={isArabic ? 'البريد (اختياري)' : 'Email (optional)'} />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder={isArabic ? 'ملاحظات' : 'Notes'} className="w-full rounded-md bg-secondary/50 border border-white/10 px-3 py-2 text-sm" />
            {!editing && (
              <>
                <Input type="number" min="0" value={form.initial_points} onChange={(e) => setForm({ ...form, initial_points: parseInt(e.target.value || 0) })} placeholder={isArabic ? 'نقاط الترحيب' : 'Welcome points'} data-testid="welcome-points" />
                <div className="space-y-1">
                  <Input
                    value={form.referrer_membership_number}
                    onChange={(e) => setForm({ ...form, referrer_membership_number: e.target.value.trim() })}
                    placeholder={isArabic ? 'رقم عضوية الشخص الذي عرّفك بالصالون (اختياري)' : 'Referrer membership # (optional)'}
                    data-testid="referrer-membership-input"
                    className="border-amber-500/30"
                  />
                  <p className="text-[10px] text-amber-400/80">
                    {isArabic
                      ? '🎁 إذا أتمّ هذا العضو 3 زيارات متتالية، يحصل المُحيل على +20 نقطة وأنت على +10 نقاط (بانتظار اعتماد المدير)'
                      : '🎁 If this new member completes 3 consecutive visits, the referrer earns +20 pts and the new member earns +10 pts (pending admin approval)'}
                  </p>
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>{isArabic ? 'إلغاء' : 'Cancel'}</Button>
              <Button onClick={saveMember} disabled={saving} data-testid="save-member">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isArabic ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Award modal */}
      {awardMember && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full space-y-3" dir={isArabic ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              {isArabic ? 'إضافة نقاط' : 'Award Points'} — {awardMember.name}
            </h3>
            <div className="text-xs text-muted-foreground">
              {isArabic ? 'سيتم إضافة بونص المستوى تلقائياً' : 'Level bonus auto-applied'}: <span className="font-bold" style={{ color: awardMember.level.color }}>+{awardMember.level.bonus_pct}%</span>
            </div>
            <Input type="number" value={awardData.points} onChange={(e) => setAwardData({ ...awardData, points: e.target.value })} placeholder={isArabic ? 'النقاط *' : 'Points *'} data-testid="award-points-input" />
            <Input value={awardData.service_name} onChange={(e) => setAwardData({ ...awardData, service_name: e.target.value })} placeholder={isArabic ? 'اسم الخدمة' : 'Service'} />
            <Input type="number" step="0.01" value={awardData.amount_aed} onChange={(e) => setAwardData({ ...awardData, amount_aed: e.target.value })} placeholder={isArabic ? 'المبلغ المدفوع (د.إ)' : 'Amount (AED)'} />
            <Input value={awardData.note} onChange={(e) => setAwardData({ ...awardData, note: e.target.value })} placeholder={isArabic ? 'ملاحظة' : 'Note'} />
            <div className="space-y-1">
              <Input
                value={awardData.reason}
                onChange={(e) => setAwardData({ ...awardData, reason: e.target.value })}
                placeholder={isArabic
                  ? (isAdmin ? 'سبب (اختياري)' : 'سبب إضافة النقاط *')
                  : (isAdmin ? 'Reason (optional)' : 'Reason for points *')
                }
                data-testid="award-reason-input"
                className={!isAdmin ? 'border-yellow-500/50' : ''}
              />
              {!isAdmin && (
                <p className="text-[10px] text-yellow-400">
                  {isArabic
                    ? 'سيُرسل الطلب للمدير ولن تُضاف النقاط حتى يعتمد الطلب.'
                    : 'Request will be sent to the admin; points are credited only after approval.'}
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAwardMember(null)} disabled={awarding}>{isArabic ? 'إلغاء' : 'Cancel'}</Button>
              <Button onClick={submitAward} disabled={awarding} data-testid="submit-award">
                {awarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {isArabic ? 'إضافة' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Redeem modal */}
      {redeemMember && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-sm w-full space-y-3" dir={isArabic ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Minus className="w-5 h-5 text-blue-500" />
              {isArabic ? 'استبدال نقاط' : 'Redeem'} — {redeemMember.name}
            </h3>
            <div className="text-xs">
              {isArabic ? 'الرصيد المتاح:' : 'Available:'} <span className="font-bold text-amber-500">{redeemMember.points_balance}</span> ({redeemMember.redeemable_aed} د.إ)
            </div>
            <div className="text-xs text-muted-foreground">{isArabic ? 'كل 100 نقطة = 7 د.إ' : '100 pts = 7 AED'}</div>
            <Input type="number" value={redeemPoints} onChange={(e) => setRedeemPoints(e.target.value)} placeholder={isArabic ? 'النقاط المراد استبدالها' : 'Points to redeem'} data-testid="redeem-input" />
            <div className="text-sm text-emerald-500">
              ≈ {((parseInt(redeemPoints || 0) / 100) * 7).toFixed(2)} د.إ
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRedeemMember(null)} disabled={redeeming}>{isArabic ? 'إلغاء' : 'Cancel'}</Button>
              <Button onClick={submitRedeem} disabled={redeeming} data-testid="submit-redeem">
                {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : (isArabic ? 'استبدال' : 'Redeem')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {historyMember && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <History className="w-5 h-5" />
                {historyMember.name}
              </h3>
              <button onClick={() => setHistoryMember(null)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            {/* Tabs */}
            <div className="flex gap-2 border-b border-white/10 pb-2 mb-3">
              <Button size="sm" variant={historyTab === 'points' ? 'default' : 'ghost'} onClick={() => setHistoryTab('points')} data-testid="hist-tab-points">
                {isArabic ? 'سجل النقاط' : 'Points History'}
              </Button>
              <Button size="sm" variant={historyTab === 'extensions' ? 'default' : 'ghost'} onClick={() => setHistoryTab('extensions')} data-testid="hist-tab-extensions">
                {isArabic ? `سجل التمديدات (${extensionsHistory.length})` : `Extensions (${extensionsHistory.length})`}
              </Button>
            </div>
            {historyTab === 'points' ? (
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-2 px-2">{isArabic ? 'النوع' : 'Type'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'النقاط' : 'Points'}</th>
                  <th className="text-start py-2 px-2">{isArabic ? 'الخدمة/ملاحظة' : 'Service/Note'}</th>
                  <th className="text-start py-2 px-2">{isArabic ? 'التاريخ' : 'Date'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'تنتهي' : 'Expires'}</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">-</td></tr>
                ) : history.map((tx) => (
                  <tr key={tx.id} className="border-t border-white/5">
                    <td className={`py-2 px-2 font-semibold ${tx.type === 'earn' ? 'text-emerald-500' : 'text-blue-500'}`}>
                      {tx.type === 'earn' ? (isArabic ? 'كسب' : 'Earn') : (isArabic ? 'استبدال' : 'Redeem')}
                    </td>
                    <td className="py-2 px-2 text-center font-bold">{tx.type === 'earn' ? '+' : '-'}{tx.points}</td>
                    <td className="py-2 px-2 text-muted-foreground text-xs">{tx.service_name || tx.note || '-'}</td>
                    <td className="py-2 px-2 text-muted-foreground text-xs">{(tx.created_at || '').slice(0, 10)}</td>
                    <td className="py-2 px-2 text-center text-xs text-muted-foreground">{(tx.expires_at || '').slice(0, 10) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            ) : (
            <table className="w-full text-sm" data-testid="extensions-history-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-2 px-2">{isArabic ? 'الحالة' : 'Status'}</th>
                  <th className="text-start py-2 px-2">{isArabic ? 'الطالب' : 'Requested by'}</th>
                  <th className="text-start py-2 px-2">{isArabic ? 'المعتمد' : 'Decided by'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'تاريخ الطلب' : 'Requested'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'تاريخ القرار' : 'Decided'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'حتى' : 'Expires'}</th>
                  <th className="text-center py-2 px-2">{isArabic ? 'استُهلك' : 'Consumed'}</th>
                </tr>
              </thead>
              <tbody>
                {extensionsHistory.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">{isArabic ? 'لا توجد تمديدات سابقة' : 'No extensions yet'}</td></tr>
                ) : extensionsHistory.map((ex) => {
                  const statusMap = {
                    pending: { label: isArabic ? 'معلّق' : 'Pending', cls: 'bg-yellow-500/20 text-yellow-500' },
                    active: { label: isArabic ? 'ساري' : 'Active', cls: 'bg-blue-500/20 text-blue-500' },
                    consumed: { label: isArabic ? 'مُستخدم' : 'Consumed', cls: 'bg-emerald-500/20 text-emerald-500' },
                    expired: { label: isArabic ? 'منتهي' : 'Expired', cls: 'bg-gray-500/20 text-gray-500' },
                    rejected: { label: isArabic ? 'مرفوض' : 'Rejected', cls: 'bg-red-500/20 text-red-500' },
                  };
                  const s = statusMap[ex.status] || statusMap.pending;
                  return (
                    <tr key={ex.id} className="border-t border-white/5">
                      <td className="py-2 px-2"><span className={`inline-block px-2 py-0.5 rounded text-xs ${s.cls}`}>{s.label}</span></td>
                      <td className="py-2 px-2 text-xs">{ex.requested_by_name || '-'}</td>
                      <td className="py-2 px-2 text-xs">{ex.decided_by_name || '-'}</td>
                      <td className="py-2 px-2 text-center text-xs text-muted-foreground">{(ex.requested_at || '').slice(0, 10)}</td>
                      <td className="py-2 px-2 text-center text-xs text-muted-foreground">{(ex.decided_at || '').slice(0, 10) || '-'}</td>
                      <td className="py-2 px-2 text-center text-xs text-muted-foreground">{(ex.expires_at || '').slice(0, 10) || '-'}</td>
                      <td className="py-2 px-2 text-center text-xs text-muted-foreground">{(ex.consumed_at || '').slice(0, 10) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}

      {/* Referral History modal */}
      {referralMember && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl bg-card border-amber-500/30 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-white/10">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  {isArabic ? 'سجل الإحالة' : 'Referral History'} — {referralMember.name}
                </h2>
                <div className="text-xs text-muted-foreground mt-1">#{referralMember.membership_number}</div>
              </div>
              <button onClick={() => setReferralMember(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {referralLoading || !referralData ? (
                <div className="text-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin inline" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                    {[
                      { label: isArabic ? 'إجمالي الإحالات' : 'Total Referrals', value: referralData.referred_count, color: 'text-blue-400' },
                      { label: isArabic ? 'أتمّوا 3 زيارات' : 'Completed 3 Visits', value: referralData.referred_count_completed, color: 'text-emerald-400' },
                      { label: isArabic ? 'نقاط مُعتمدة' : 'Earned', value: referralData.total_referral_points_earned, color: 'text-amber-400' },
                      { label: isArabic ? 'نقاط معلّقة' : 'Pending', value: referralData.total_referral_points_pending, color: 'text-orange-400' },
                    ].map((s) => (
                      <div key={s.label} className="bg-secondary/30 rounded-lg p-3 text-center">
                        <div className="text-[10px] text-muted-foreground">{s.label}</div>
                        <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {referralMember.referrer_name && (
                    <div className="text-xs bg-secondary/20 rounded p-2 mb-3">
                      <span className="text-muted-foreground">{isArabic ? 'تمّت إحالته من: ' : 'Referred by: '}</span>
                      <span className="font-bold">{referralMember.referrer_name}</span>
                      <span className="text-muted-foreground ms-1">#{referralMember.referrer_membership_number}</span>
                    </div>
                  )}

                  {referralData.referred_members && referralData.referred_members.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary/40">
                          <tr>
                            <th className="text-start py-2 px-2">{isArabic ? 'الاسم' : 'Name'}</th>
                            <th className="text-center py-2 px-2">#</th>
                            <th className="text-center py-2 px-2">{isArabic ? 'تاريخ الانضمام' : 'Joined'}</th>
                            <th className="text-center py-2 px-2">{isArabic ? 'الزيارات' : 'Visits'}</th>
                            <th className="text-center py-2 px-2">{isArabic ? 'الحالة' : 'Status'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referralData.referred_members.map((r) => (
                            <tr key={r.id} className="border-t border-white/5">
                              <td className="py-2 px-2 font-semibold">{r.name}</td>
                              <td className="py-2 px-2 text-center font-mono">#{r.membership_number}</td>
                              <td className="py-2 px-2 text-center text-muted-foreground">
                                {r.joined_at ? new Date(r.joined_at).toLocaleDateString(isArabic ? 'ar-AE' : 'en-GB') : '-'}
                              </td>
                              <td className="py-2 px-2 text-center">{r.consecutive_visits_count || 0}</td>
                              <td className="py-2 px-2 text-center">
                                {r.referral_bonus_triggered ? (
                                  <span className="text-emerald-400">✓ {isArabic ? 'مكتملة' : 'Completed'}</span>
                                ) : (
                                  <span className="text-amber-400">{isArabic ? 'قيد الإكمال' : 'In progress'}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      {isArabic ? 'لا توجد إحالات بعد' : 'No referrals yet'}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 border-t border-white/10 flex flex-wrap gap-2 items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {canPrintReferral
                  ? (isArabic ? '✅ مفعّل للطباعة' : '✅ Print enabled')
                  : (isArabic ? '⚠️ يحتاج اعتماد المدير للطباعة' : '⚠️ Admin approval required')}
              </div>
              <div className="flex gap-2">
                {!canPrintReferral && !isAdmin && (
                  <Button onClick={requestReferralPrint} size="sm" variant="outline" className="border-amber-500/40" data-testid="request-referral-print">
                    <FileText className="w-4 h-4 me-1" />
                    {isArabic ? 'طلب اعتماد للطباعة' : 'Request approval'}
                  </Button>
                )}
                {canPrintReferral && (
                  <>
                    <Button size="sm" variant={referralPrintMode === 'summary' ? 'default' : 'ghost'} onClick={() => setReferralPrintMode('summary')}>
                      {isArabic ? 'ملخّص' : 'Summary'}
                    </Button>
                    <Button size="sm" variant={referralPrintMode === 'detailed' ? 'default' : 'ghost'} onClick={() => setReferralPrintMode('detailed')}>
                      {isArabic ? 'مفصّل' : 'Detailed'}
                    </Button>
                    <Button onClick={handlePrintReferral} size="sm" className="gap-2 bg-amber-500 hover:bg-amber-600 text-black" data-testid="print-referral-btn">
                      <Printer className="w-4 h-4" />
                      {isArabic ? 'تنزيل PDF' : 'Download PDF'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>

          <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
            {referralData && (
              <ReferralHistorySheet
                ref={referralPrintRef}
                member={referralMember}
                data={referralData}
                mode={referralPrintMode}
                requestedBy={user?.name}
                approvedBy={isAdmin ? user?.name : undefined}
              />
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default LoyaltyAdmin;
